<script setup lang="ts">
import VarsityInlineConfirm from "@/components/VarsityInlineConfirm.vue"
import VarsityInlineConfirmButton from "@/components/VarsityInlineConfirmButton.vue"
import VarsityInlineConfirmGroup from "@/components/VarsityInlineConfirmGroup.vue"
import { createInlineConfirmCollection } from "@/composables/createInlineConfirm"
import type { ApproveAddressUpdateTask } from "@/model/app/approve-address-update-task"
import { useHttpClient } from "@/vf"
import type { TasksProps } from "./tasks-props"

const props = defineProps<TasksProps<ApproveAddressUpdateTask>>()
const emit = defineEmits<{
    (e: "updated"): void
}>()
const http = useHttpClient()

const getInlineConfirmController = createInlineConfirmCollection(id => ({
    async action() {
        await http.post(`/account/${props.issue.owner!.id}/contact/task/${props.task.id}/approve-update`, {
            approved: id === "approve",
        })
    },
    afterAction() {
        emit("updated")
    },
}))
</script>

<template>
    <div class="p-4">
        <div class="row">
            <div class="col-md-4">
                <div>
                    <b>{{ $t("@tasks:tasks.approve_address_update.original") }}</b>
                </div>
                <div>{{ props.task.originalValues.addressLine }}</div>
                <div>{{ props.task.originalValues.addressLine2 }}</div>
                <div>{{ props.task.originalValues.street }} {{ props.task.originalValues.streetNumber }}</div>
                <div>{{ props.task.originalValues.zip }} {{ props.task.originalValues.city }}</div>
                <div>{{ props.task.originalValues.country }}</div>
            </div>
            <div class="col-md-4">
                <div>
                    <b>{{ $t("@tasks:tasks.approve_address_update.new") }}</b>
                </div>
                <div>{{ props.task.newValues.addressLine }}</div>
                <div>{{ props.task.newValues.addressLine2 }}</div>
                <div>{{ props.task.newValues.street }} {{ props.task.newValues.streetNumber }}</div>
                <div>{{ props.task.newValues.zip }} {{ props.task.newValues.city }}</div>
                <div>{{ props.task.newValues.country }}</div>
            </div>
            <div class="col-md-4">
                <div v-if="props.viewMode === 'admin' && props.task.state === 'open'">
                    <VarsityInlineConfirmGroup>
                        <VarsityInlineConfirmButton :controller="getInlineConfirmController('approve')">
                            {{ $t("@tasks:tasks.approve_address_update.approve.button") }}
                        </VarsityInlineConfirmButton>
                        <VarsityInlineConfirmButton
                            :controller="getInlineConfirmController('decline')"
                            class="btn btn-danger btn-shape-skewed"
                        >
                            {{ $t("@tasks:tasks.approve_address_update.decline.button") }}
                        </VarsityInlineConfirmButton>
                        <VarsityInlineConfirm :controller="getInlineConfirmController('approve')">
                            <template #confirmation>
                                {{ $t("@tasks:tasks.approve_address_update.approve.confirmation") }}
                            </template>
                            <template #success>
                                {{ $t("@tasks:tasks.approve_address_update.approve.success") }}
                            </template>
                        </VarsityInlineConfirm>
                        <VarsityInlineConfirm :controller="getInlineConfirmController('decline')">
                            <template #confirmation>
                                {{ $t("@tasks:tasks.approve_address_update.decline.confirmation") }}
                            </template>
                            <template #success>
                                {{ $t("@tasks:tasks.approve_address_update.decline.success") }}
                            </template>
                        </VarsityInlineConfirm>
                    </VarsityInlineConfirmGroup>
                </div>
            </div>
        </div>
    </div>
</template>
